import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GroupService } from '../services/group.service';
import { UserService } from '../services/user.service';
import { createUsageTracker, type FlowStage, type UsageTracker } from './usage-tracker';
import { routePatternOf } from './usage-route';

interface UsagePass {
  pass: string;
  endpoint: string;
}

/** How long the pass request may take before it is given up on. */
const PASS_WAIT_MS = 10_000;

/**
 * Usage tracking for REMOAT (see usage-tracker.ts): runs while someone is signed in, with a pass for the practice they
 * are working in. It must be invisible whether it works or not: it runs outside Angular's zone, asks for its pass with
 * plain fetch (so no loader, interceptor or toast ever sees it), and every method swallows its own failures. Nothing is
 * sent when the clinical backend has no REMOAT address (the pass request answers 404).
 */
@Injectable({ providedIn: 'root' })
export class UsageService {
  private tracker: UsageTracker | null = null;
  private userId: string | null = null;
  private groupId: string | null = null;
  private starting = false;
  /** The practice changed while the tracker was being started: its first pass names the old one. */
  private changedWhileStarting = false;

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    userService: UserService,
    groupService: GroupService,
  ) {
    try {
      combineLatest([
        userService.userProfile$.pipe(map((user) => user?.id ?? null)),
        groupService.selectedGroup$.pipe(map((group) => group?.id ?? null)),
      ]).pipe(distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]))
        .subscribe({ next: ([userId, groupId]) => this.quietly(() => this.follow(userId, groupId)), error: () => undefined });

      router.events.pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe({ next: () => this.quietly(() => this.tracker?.navigated()), error: () => undefined });
    } catch {
      // Tracking off for this page; the app is unaffected.
    }
  }

  /** A named piece of work started or finished, e.g. flow('invoice', 'done'). */
  flow(name: string, stage: FlowStage): void {
    this.quietly(() => this.tracker?.flow(name, stage));
  }

  /** Something the app names itself, counted with the clicks on the current page. */
  action(name: string): void {
    this.quietly(() => this.tracker?.action(name));
  }

  error(error: unknown): void {
    this.quietly(() => this.tracker?.error(error));
  }

  /** One server call, timed by the interceptor. */
  api(method: string, url: string, ms: number, status: number): void {
    this.quietly(() => this.tracker?.api(method, url, ms, status));
  }

  private quietly(work: () => void): void {
    try {
      work();
    } catch {
      // Usage tracking never surfaces a failure.
    }
  }

  private follow(userId: string | null, groupId: string | null): void {
    this.userId = userId;
    this.groupId = groupId;
    if (!userId) {
      // Signed out: send what is left with the pass still held, and stop.
      const tracker = this.tracker;
      this.tracker = null;
      if (tracker) {
        void tracker.flush();
        tracker.stop();
      }
      return;
    }
    if (this.tracker) {
      this.tracker.passChanged();
      return;
    }
    if (this.starting) {
      this.changedWhileStarting = true;
      return;
    }
    void this.start().catch(() => undefined);
  }

  private async start(): Promise<void> {
    this.starting = true;
    this.changedWhileStarting = false;
    const first = await this.fetchPass().finally(() => (this.starting = false));
    // Signed out while the pass was being fetched: nothing to start.
    if (!first || this.tracker || !this.userId) return;
    let held: UsagePass | null = this.changedWhileStarting ? null : first;
    this.zone.runOutsideAngular(() => {
      this.tracker = createUsageTracker({
        endpoint: first.endpoint,
        appVersion: environment.APP_VERSION,
        route: () => routePatternOf(this.router.routerState.snapshot.root),
        getPass: async () => {
          // The pass fetched to start with is used first; later ones are fetched as they near expiry.
          const pass = held ?? await this.fetchPass();
          held = null;
          return pass?.pass ?? null;
        },
      });
      this.tracker.start();
    });
  }

  /** The usage pass from clinical's own API, or null on any failure (including no REMOAT here). Never throws. */
  private async fetchPass(): Promise<UsagePass | null> {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const base = (environment.API_URL || '').replace(/\/+$/, '');
      const query = this.groupId ? `?groupId=${encodeURIComponent(this.groupId)}` : '';
      const abort = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = setTimeout(() => abort?.abort(), PASS_WAIT_MS);
      let giveUp: ReturnType<typeof setTimeout> | undefined;
      try {
        const reply = await Promise.race([
          fetch(`${base}/api/remoat/usage-pass${query}`, { headers: { Authorization: `Bearer ${token}` }, ...(abort ? { signal: abort.signal } : {}) }),
          new Promise<null>((resolve) => (giveUp = setTimeout(() => resolve(null), PASS_WAIT_MS + 1000))),
        ]);
        if (!reply || reply.status !== 200) return null;
        const body = await reply.json() as Partial<UsagePass> | null;
        return body && typeof body.pass === 'string' && typeof body.endpoint === 'string' ? { pass: body.pass, endpoint: body.endpoint } : null;
      } finally {
        clearTimeout(timer);
        clearTimeout(giveUp);
      }
    } catch {
      return null;
    }
  }
}
